import Phaser from 'phaser';

import { TwentyOneMainScene } from '../../scenes/twentyone-main-scene';
import { Subscription } from 'rxjs';
import { ResizeMetrics } from '../../model/resize-metrics';
import { TwentyOne, GameEvent } from 'game-logic';
import { UxMetrics } from '../../model/ux-metrics';
import { ColumnViewModel } from './column-view-model';
import { Positioning } from '../../model/positioning';
import { ColumnStackView } from './column-stack-view';
import { ScoreLabelView } from '../components/score-label-view';
import { AvailableFontSizes, TextService } from '../../services/text-service';
// import { DebugContainerVisual } from '../debug/debug-container-visual';

export class ColumnView extends Phaser.GameObjects.Container {
  private currentResizeMetrics?: ResizeMetrics;
  private subscriptionGroup: Subscription;

  private scoreImage: Phaser.GameObjects.Image;
  private columnBoxImage: Phaser.GameObjects.Image;
  private wildSparkleImage: Phaser.GameObjects.Image;

  private columnStackView: ColumnStackView;
  private hintLabelView: ScoreLabelView;
  private scoreLabelView: ScoreLabelView;

  private fadeValue = 0;

  constructor(
    scene: TwentyOneMainScene,
    x: number,
    y: number,
    private viewModel: ColumnViewModel,
  ) {
    super(scene, x, y);

    // new DebugContainerVisual(scene, this);

    this.subscriptionGroup = new Subscription();

    this.scoreImage = scene.add
      .image(0, 0, viewModel.scoreBoxAsset.type)
      .setOrigin(0.5, 0.5)
      .setAlpha(0);
    this.add(this.scoreImage);

    this.columnBoxImage = scene.add
      .image(0, 0, viewModel.columnBoxAsset.type)
      .setOrigin(0.5, 0.5)
      .setAlpha(0);
    this.add(this.columnBoxImage);

    this.wildSparkleImage = scene.add
      .image(0, 0, viewModel.wildSparkleAsset.type)
      .setOrigin(0.5, 0.5)
      .setTint(this.viewModel.wildSymbolColor)
      .setAlpha(0);
    this.add(this.wildSparkleImage);

    this.columnStackView = new ColumnStackView(scene, 0, 0, viewModel.columnStackViewModel);
    this.add(this.columnStackView);

    this.hintLabelView = new ScoreLabelView(scene, 0, 0, viewModel.hintLabelViewModel);
    this.add(this.hintLabelView);

    this.scoreLabelView = new ScoreLabelView(scene, 0, 0, viewModel.scoreLabelViewModel);
    this.add(this.scoreLabelView);

    this.setName(ColumnView.name);
    scene.add.existing(this);

    this.setupProgress(0);

    this.subscriptionGroup.add(
      this.viewModel.onGameSetupStart$.subscribe(() => {
        this.scene.tweens.add({
          targets: this,
          fadeValue: 1,
          duration: UxMetrics.LoadingFade,
          ease: Phaser.Math.Easing.Quadratic.InOut,
          onUpdate: (_tween) => {
            this.setupProgress(this.fadeValue);
          },
        });
      }),
    );
    this.subscriptionGroup.add(
      viewModel.onResetGameScene$.subscribe(() => {
        this.setupProgress(0);
      }),
    );
    this.subscriptionGroup.add(
      viewModel.logicEventQueue$.subscribe((eventQueue) => {
        eventQueue?.subscribe(this.handleLogicEvents.bind(this));
      }),
    );
  }

  override destroy(fromScene?: boolean | undefined): void {
    this.subscriptionGroup.unsubscribe();

    super.destroy(fromScene);
  }

  public onResize(resizeMetrics: ResizeMetrics) {
    this.currentResizeMetrics = resizeMetrics;

    this.columnStackView?.onResize(resizeMetrics);
    this.hintLabelView?.onResize(resizeMetrics);
    this.scoreLabelView?.onResize(resizeMetrics);

    this.refreshLayout(resizeMetrics);
  }

  private setupProgress(progress: number): void {
    this.scoreImage.setAlpha(progress);
    this.columnBoxImage.setAlpha(progress);
  }

  private refreshLayout(resizeMetrics: ResizeMetrics): void {
    const columnRect = Positioning.globalColumnRect(
      this.viewModel.columnIndex,
      resizeMetrics.layoutConfig,
      resizeMetrics.scalingMetrics,
    );

    this.setSize(columnRect.width, columnRect.height);
    this.setPosition(columnRect.pos.x, columnRect.pos.y);

    const columnHintRect = Positioning.localColumnHintRect(
      resizeMetrics.layoutConfig,
      resizeMetrics.scalingMetrics,
    );
    this.hintLabelView.setPosition(columnHintRect.pos.x, columnHintRect.pos.y);
    this.wildSparkleImage.setPosition(columnHintRect.pos.x, columnHintRect.pos.y);
    this.wildSparkleImage.setDisplaySize(columnHintRect.height * 0.5, columnHintRect.height * 0.5);

    const columnScoreRect = Positioning.localColumnScoreRect(
      resizeMetrics.layoutConfig,
      resizeMetrics.scalingMetrics,
    );
    this.scoreImage.setPosition(columnScoreRect.pos.x, columnScoreRect.pos.y);
    this.scoreImage.setDisplaySize(columnScoreRect.width, columnScoreRect.height);
    this.scoreLabelView.setPosition(columnScoreRect.pos.x, columnScoreRect.pos.y);

    const columnStackRect = Positioning.localColumnStackRect(
      resizeMetrics.layoutConfig,
      resizeMetrics.scalingMetrics,
    );
    this.columnBoxImage.setPosition(columnStackRect.pos.x, columnStackRect.pos.y);
    this.columnBoxImage.setDisplaySize(columnStackRect.width, columnStackRect.height);
    this.columnStackView.setPosition(columnStackRect.pos.x, columnStackRect.pos.y);
  }

  private async handleLogicEvents(event: GameEvent<TwentyOne.TwentyOneEvents>): Promise<void> {
    switch (event.type) {
      case 'onRoundStateChanged':
        await this.columnStackView.handleLogicEvents(event);
        break;
      case 'onBeforeMoveToColumn':
        this.viewModel.updateHint('', event.instant);
        await this.columnStackView.handleLogicEvents(event);
        break;
      case 'onMoveCurrentToColumn':
        {
          const column = event.payload.cardColumn;
          if (column.pileId === this.viewModel.columnId) {
            this.viewModel.updateScore(column.displayScore, event.instant);
            await this.columnStackView.handleLogicEvents(event);
          }
        }
        break;
      case 'onColumnResult':
        {
          const column = event.payload.cardColumn;
          if (column.pileId === this.viewModel.columnId) {
            if (event.instant) {
              break;
            }

            const cardCount = column.cardCount;
            const points = event.payload.points;

            switch (event.payload.result) {
              case TwentyOne.PlayerActionResult.Stack:
                this.createPointsLabel(cardCount, `+${points}`, 0, false);
                break;
              case TwentyOne.PlayerActionResult.Bust:
                this.createPointsLabel(cardCount, `${points}`, UxMetrics.BonusClearDelay, true);
                break;
              case TwentyOne.PlayerActionResult.ReachedTarget:
                this.createPointsLabel(cardCount, `+${points}`, UxMetrics.BonusClearDelay, true);
                break;
              case TwentyOne.PlayerActionResult.Wild:
                this.createPointsLabel(cardCount, `+${points}`, UxMetrics.BonusClearDelay, true);
                break;
            }
          }
        }
        break;
      case 'onColumnCleared':
        {
          const column = event.payload.cardColumn;
          if (column.pileId === this.viewModel.columnId) {
            this.viewModel.updateScore('0', event.instant);
            await this.columnStackView.handleLogicEvents(event);
          }
        }
        break;
      case 'onDealComplete':
        {
          // Show hint score
          const column = event.payload.cardColumns.get(this.viewModel.columnId);
          if (column) {
            this.viewModel.updateScore('0', event.instant);
            await this.columnStackView.handleLogicEvents(event);

            this.updateHint(column, event.payload.cardStackCurrent, event.instant);
          }
        }
        break;
      case 'onShowHint':
        {
          // Show hint score
          const column = event.payload.cardColumns.get(this.viewModel.columnId);
          if (column) {
            this.updateHint(column, event.payload.cardStackCurrent, event.instant);
          }
        }
        break;
      case 'onAfterMoveToColumn':
        {
          // Show hint score
          const column = event.payload.cardColumns.get(this.viewModel.columnId);
          if (column) {
            await this.columnStackView.handleLogicEvents(event);
          }
        }
        break;
      default:
        break;
    }
  }

  private updateHint(
    column: TwentyOne.CardColumn,
    currentCard: TwentyOne.CardStack,
    instant: boolean,
  ): void {
    const scoreHint = column.calculateScoreHint(currentCard.allFaceCards);
    const nextMoveIsBust = TwentyOne.TwentyOneScoring.hasBust(scoreHint);
    const isWild = currentCard.containsWild;
    this.viewModel.updateHint(
      nextMoveIsBust || isWild ? '' : TwentyOne.TwentyOneScoring.displayScore(scoreHint),
      instant,
    );
    this.wildSparkleImage.setAlpha(isWild ? this.viewModel.wildSymbolAlpha : 0.0);
  }

  private createPointsLabel(
    cardCount: number,
    result: string,
    delay: number,
    isFinalScore: boolean,
  ) {
    if (!this.currentResizeMetrics) {
      return;
    }

    const fontStyle = TextService.fontStyleBold(
      isFinalScore ? AvailableFontSizes.XXLarge : AvailableFontSizes.Large,
      this.viewModel.bonusFillColor,
      this.viewModel.defaultFontFamily,
    );

    const cardPosition = Positioning.localCardStackPosition(
      cardCount - 1,
      this.currentResizeMetrics.layoutConfig,
      this.currentResizeMetrics.scalingMetrics,
    );
    cardPosition.y +=
      this.currentResizeMetrics.layoutConfig.cardHeight *
      0.5 *
      this.currentResizeMetrics.scalingMetrics.scaleFactor;

    const columnPosition = new Phaser.Math.Vector2();
    this.getWorldTransformMatrix().transformPoint(cardPosition.x, cardPosition.y, columnPosition);

    const centerPosition = Positioning.globalCenterPosition(
      this.currentResizeMetrics.layoutConfig,
      this.currentResizeMetrics.scalingMetrics,
    );

    const pointsLabel = this.scene.add
      .text(columnPosition.x, columnPosition.y, result, fontStyle)
      .setFontSize(
        isFinalScore
          ? this.currentResizeMetrics.fontMetrics.XXLarge
          : this.currentResizeMetrics.fontMetrics.Large,
      )
      .setOrigin(0.5)
      .setDepth(500)
      .setAlpha(0)
      .setStroke(this.viewModel.bonusOutlineColor, 10);

    const targetX = centerPosition.x;
    const targetY = 0;

    this.scene.tweens.add({
      targets: pointsLabel,
      alpha: 1.0,
      scale: 1.0,
      duration: UxMetrics.BonusFadeDuration,
      delay: delay,
      ease: Phaser.Math.Easing.Quartic.Out,
      onComplete: () => {
        this.scene.tweens.add({
          targets: pointsLabel,
          alpha: 0.5,
          x: targetX,
          y: targetY,
          scale: 0.5,
          duration: UxMetrics.BonusTravelDuration,
          ease: Phaser.Math.Easing.Quartic.In,
          onComplete: () => {
            pointsLabel.destroy();
          },
        });
      },
    });
  }
}
